import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  Alert,
  IconButton,
  Collapse,
  Paper,
  SnackbarContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export interface WidgetListPopUpProps {
  allWidgetsByType: any;
  statusClicked: any;
  handelCLickEditWidget: any;
  deviceEdited: string | null;
  setOpen: any;
  setisPublishOpen: any;
  setIsElementSaved: any;
  selectedWidgettype: any;
  shareUidAvailable: any;
  onAddWidgets: any;
  handleCloseWidgetPopup: any;
  setAllWidgetsByType: any;
  siteAllAvailableWidgets: any;
  setSelectedWidgetType: any;
  widgetRecords: any;
  selectedWidgetType: any;
  setOpenWidgetPopup: any;
}

export default function WidgetListPopUp({
  allWidgetsByType,
  statusClicked,
  deviceEdited,
  handelCLickEditWidget,
  setOpen,
  setisPublishOpen,
  setIsElementSaved,
  selectedWidgettype,
  shareUidAvailable, // getting the SharedUid of the widget when editing
  onAddWidgets,
  handleCloseWidgetPopup,
  setAllWidgetsByType,
  siteAllAvailableWidgets,
  setSelectedWidgetType,
  widgetRecords,
  selectedWidgetType,
  setOpenWidgetPopup,
}: WidgetListPopUpProps) {
  const [widgetSelected, setWidgetSelected] = useState<(string | null)[]>([
    null,
  ]);
  const [checked, setChecked] = useState([]) as any;
  const [errorSelect, setErrorSelect] = useState<string>();
  const [errorCheckbox, setErrorCheckbox] = useState<string>();
  const [openSelectAlert, setOpenSelectAlert] = useState<boolean>(true);
  const [openCheckboxAlert, setOpenCheckboxAlert] = useState<boolean>(true);

  const safeShareUids = shareUidAvailable || [];

  const initialCount =
    safeShareUids.length === 5
      ? 5
      : safeShareUids.length === 4
      ? 4
      : safeShareUids.length === 3
      ? 3
      : safeShareUids.length === 2
      ? 2
      : 1;

  const [autocompleteCount, setAutocompleteCount] =
    useState<number>(initialCount);
  const [autocompleteKeys, setAutocompleteKeys] = useState<string[]>(
    Array.from({ length: initialCount }, () => uuidv4())
  );

  const onChecked = (value: boolean, device: string) => {
    const newCheck = { ...checked };
    if (value) {
      newCheck[device] = true;
    } else {
      delete newCheck[device];
    }
    setChecked(newCheck);
  };

  useEffect(() => {
    if (errorSelect) {
      setOpenSelectAlert(true);
      const timer = setTimeout(() => setOpenSelectAlert(false), 3000); // 3 seconds timer
      return () => clearTimeout(timer);
    }
  }, [errorSelect]);

  useEffect(() => {
    if (errorCheckbox) {
      setOpenCheckboxAlert(true);
      const timer = setTimeout(() => setOpenCheckboxAlert(false), 3000); // 3 seconds timer
      return () => clearTimeout(timer);
    }
  }, [errorCheckbox]);

  // Reset state when not editing (for new widget creation)
  useEffect(() => {
    if (!deviceEdited && !shareUidAvailable?.length) {
      setWidgetSelected([null]);
      setChecked({});
      setAutocompleteCount(1);
      setAutocompleteKeys([uuidv4()]);
      setErrorSelect("");
      setErrorCheckbox("");
      setOpenSelectAlert(false);
      setOpenCheckboxAlert(false);
    }
  }, [deviceEdited, shareUidAvailable]);

  useEffect(() => {
    if (deviceEdited) {
      const checkedValues =
        deviceEdited === "desktop"
          ? { desktop: true }
          : deviceEdited === "mobile"
          ? { mobile: true }
          : { tablet: true };

      setChecked(checkedValues);

      // Set widget selection properly for editing
      if (shareUidAvailable && shareUidAvailable.length > 0) {
        setWidgetSelected(shareUidAvailable);
        setAutocompleteCount(shareUidAvailable.length);
        setAutocompleteKeys(
          Array.from({ length: shareUidAvailable.length }, () => uuidv4())
        );
      }
    }
  }, [deviceEdited, shareUidAvailable]);

  const onSelectWidget = () => {
    if (validation()) {
      cleanUpWidgetSelected();

      if (statusClicked === "dragged") {
        cleanUpWidgetSelected();
        const cleaned = widgetSelected.filter((uid) => !!uid);

        // Grab all checked devices
        const selectedDevices = Object.keys(checked); // e.g., ["desktop", "tablet"]

        if (selectedDevices.length === 0) selectedDevices.push("desktop"); // Fallback

        onAddWidgets(cleaned, selectedDevices);

        setOpen(false);
        setisPublishOpen(true);
        setIsElementSaved(true);
      } else {
        // Editing existing widget - pass the device being edited
        const cleaned = widgetSelected.filter((uid) => !!uid);

        // When editing, use the deviceEdited instead of checked devices
        const devicesToUpdate = deviceEdited
          ? [deviceEdited]
          : Object.keys(checked);
        if (devicesToUpdate.length === 0) devicesToUpdate.push("desktop");

        handelCLickEditWidget(statusClicked, cleaned, devicesToUpdate);
        setOpen(false);
        setisPublishOpen(true);
        setIsElementSaved(true);
      }
    }
  };

  const validation = () => {
    let validationFlag = true;
    cleanUpWidgetSelected();
    if (!widgetSelected[0]) {
      setErrorSelect("Choose Widget");
      setOpenSelectAlert(true);
      validationFlag = false;
    } else {
      setErrorSelect("");
      setOpenSelectAlert(false);
    }
    if (deviceEdited) {
      onChecked(true, deviceEdited);
    } else if (
      !checked?.desktop &&
      !checked?.mobile &&
      !checked?.tablet &&
      !deviceEdited &&
      selectedWidgettype === "Comparison Table"
    ) {
      setErrorCheckbox("Choose Device");
      setOpenCheckboxAlert(true);
      validationFlag = false;
    } else {
      setErrorCheckbox("");
      setOpenCheckboxAlert(false);
    }
    return validationFlag;
  };

  const cleanUpWidgetSelected = () => {
    setWidgetSelected(widgetSelected.filter((widget) => widget !== null));
  };

  const handelClose = () => {
    setOpen(false);
    handleCloseWidgetPopup();

    // Reset component internal state
    setWidgetSelected([null]);
    setChecked({});
    setAutocompleteCount(1);
    setAutocompleteKeys([uuidv4()]);
    setErrorSelect("");
    setErrorCheckbox("");
    setOpenSelectAlert(false);
    setOpenCheckboxAlert(false);

    setTimeout(() => {
      setSelectedWidgetType("");
    }, 500);
  };

  const handleAddAnother = () => {
    const allSelected = Array.from({ length: autocompleteCount }).every(
      (_, index) =>
        widgetSelected[index] !== null && widgetSelected[index] !== undefined
    );

    if (allSelected && autocompleteCount < 5) {
      setAutocompleteCount(autocompleteCount + 1);
      setAutocompleteKeys([...autocompleteKeys, uuidv4()]); // Add a unique key
    } else {
      setErrorSelect("Please select a value before adding another.");
      setOpenSelectAlert(true);
    }
  };

  const handleRemoveAutocomplete = (index: number) => {
    const updatedWidgetSelected = [...widgetSelected];
    updatedWidgetSelected.splice(index, 1);

    const updatedKeys = [...autocompleteKeys];
    updatedKeys.splice(index, 1); // Remove the corresponding key

    setWidgetSelected(updatedWidgetSelected);
    setAutocompleteKeys(updatedKeys);
    setAutocompleteCount(autocompleteCount - 1);
  };

  const widgetTypes = Array.from(
    new Set(
      siteAllAvailableWidgets
        .map((w: any) => w.type)
        .filter((type: any) =>
          widgetRecords?.some((record: any) => record?.type === type)
        )
        .filter(
          (type: any) =>
            type !== "Page Structure" &&
            type !== "Popup Offer" &&
            type !== "Notification Offer"
        )
    )
  );

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center">
        <FormControl fullWidth>
          {selectedWidgettype ? (
            <Typography
              sx={{ fontSize: "16px", color: "#000000DE", fontWeight: "500" }}
            >
              Devices to show on : {deviceEdited}
            </Typography>
          ) : (
            ""
          )}

          <FormGroup row style={{ justifyContent: "space-around" }}>
            {!deviceEdited ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => onChecked(e.target.checked, "desktop")}
                      color="secondary"
                    />
                  }
                  label="Desktop (1280px)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => onChecked(e.target.checked, "tablet")}
                      color="secondary"
                    />
                  }
                  label="Tablet (768px)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(e) => onChecked(e.target.checked, "mobile")}
                      color="secondary"
                    />
                  }
                  label="Mobile (360px)"
                />
              </>
            ) : (
              ""
            )}
          </FormGroup>
          <Typography
            sx={{ fontSize: "12px", color: "#00000099", marginBottom: "20px" }}
          >
            Select at least one widget type to activate this widget.
          </Typography>

          <Autocomplete
            options={widgetTypes}
            value={selectedWidgetType}
            size="small"
            onChange={(_event, newValue) => {
              if (!newValue) return;

              const widgetType = newValue as string;
              setSelectedWidgetType(widgetType);
              setAllWidgetsByType(
                siteAllAvailableWidgets.filter(
                  (w: any) => w.type === widgetType
                )
              );
              setOpenWidgetPopup(true);
            }}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                label="Widget Type"
                variant="outlined"
                color="secondary"
                placeholder="Please select a widget type from the list."
              />
            )}
          />

          {autocompleteKeys.map((key, index) => (
            <Box key={key} display="flex" alignItems="center" gap="10px" mb={2}>
              <Autocomplete
                disabled={!selectedWidgetType}
                fullWidth
                id={`widgetShortCode-${index}`}
                disableClearable
                options={allWidgetsByType}
                getOptionLabel={(option: any) => option.widget_name}
                onChange={(_, newValue: any) => {
                  const newWidgetSelected = [...widgetSelected];
                  newWidgetSelected[index] = newValue
                    ? newValue.shared_uid
                    : null;
                  setWidgetSelected(newWidgetSelected);
                }}
                value={
                  allWidgetsByType.find(
                    (w: any) => w.shared_uid === widgetSelected[index]
                  ) || null
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    label={"Widget Name"}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    placeholder="Please select a coupon table from the list."
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper style={{ maxHeight: 200, overflow: "auto" }}>
                    {children}
                  </Paper>
                )}
              />
              {autocompleteCount > 1 && index > 0 && (
                <IconButton
                  aria-label="remove"
                  onClick={() => handleRemoveAutocomplete(index)}
                  color="secondary"
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          ))}
          {selectedWidgettype === "Comparison Table" &&
            autocompleteCount < 5 && (
              <Box
                display="flex"
                alignItems="start"
                gap="6px"
                mt={1}
                flexDirection="column"
              >
                <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                  Would you like to show more than one table?
                </Typography>
                <Button
                  color="secondary"
                  variant="outlined"
                  endIcon={<AddIcon />}
                  onClick={handleAddAnother}
                >
                  Add Another
                </Button>
              </Box>
            )}

          <Collapse in={openSelectAlert}>
            {errorSelect && (
              <Alert
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setOpenSelectAlert(false)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                style={{ marginTop: "15px" }}
              >
                {errorSelect}
              </Alert>
            )}
          </Collapse>
          <Collapse in={openCheckboxAlert}>
            {errorCheckbox && (
              <Alert
                severity="error"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="large"
                    onClick={() => setOpenCheckboxAlert(false)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                style={{ marginTop: "8px", marginBottom: "15px" }}
              >
                {errorCheckbox}
              </Alert>
            )}
          </Collapse>
          {selectedWidgettype === "Comparison Table" && (
            <SnackbarContent
              sx={{
                background: "#FFF4E5",
                color: "#ED6C02",
                marginTop: "8px",
                boxShadow: "none",
                border: "1px solid #ED6C0280",
                padding: "0 4px",
              }}
              message={
                <Box display="flex">
                  <ErrorOutlineIcon
                    sx={{ marginRight: "8px", color: "#ED6C02" }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      sx={{ marginBottom: "4px" }}
                    >
                      Please Note! When more than one table is added, table
                      rotation is automatically activated.
                    </Typography>
                  </Box>
                </Box>
              }
            />
          )}
        </FormControl>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap="6px"
        mt={2}
      >
        <Button color="secondary" onClick={handelClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => onSelectWidget()}
          endIcon={<AddIcon />}
        >
          {!deviceEdited ? "ADD" : "UPDATE"} WIDGET
        </Button>
      </Box>
    </>
  );
}
